import React from 'react';

import { useIsSmallScreen } from '../../../../system/SystemMediaQueriesProvider';
import { useMediaQuery, useTheme, Typography } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid';
import CheckIcon from '@mui/icons-material/Check';

import DataTable from '../../../shared/data-table';
import { useNavigate } from 'react-router-dom';
import {
  MobileListCard,
  MobileListDataTableProps,
} from '../../../shared/mobile-list-card';

import { ViewPropertyContentProps } from '../../types/viewPropertyContentProps';
import { MyUser as User } from './property-view';

export function PropertyAccessList({ data }: ViewPropertyContentProps) {
  const isSmall = useIsSmallScreen();

  const usersListSmall = isSmall
    ? ({
        field: 'name',
        headerName: 'Name',
        align: 'center',
        flex: 1,
        renderCell: (params: GridRenderCellParams<Partial<any>>) => {
          return (
            <MobileListCard>
              <Typography
                component="h5"
                sx={{ fontSize: 20, fontWeight: 800 }}
                color="text.secondary"
              >
                {params.row.name}
              </Typography>
              <Typography
                component="div"
                sx={{ fontSize: 16 }}
                color="text.secondary"
              >
                Email: {params.row.email}
              </Typography>
              <Typography
                component="div"
                sx={{ fontSize: 16 }}
                color="text.secondary"
              >
                Role:{' '}
                {params.row.authorizationProfile.isViewer
                  ? 'Viewer'
                  : params.row.authorizationProfile.isInstaller
                    ? 'Installer'
                    : params.row.authorizationProfile.isManager
                      ? 'Manager'
                      : null}
              </Typography>
            </MobileListCard>
          );
        },
      } as GridColDef)
    : { field: 'name', headerName: 'Name', width: 325 };

  const columns: GridColDef[] = [
    { ...usersListSmall },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      maxWidth: 350,
      renderCell: (params: GridRenderCellParams<User>) => {
        return params.row.email;
      },
    },
    {
      field: 'isViewer',
      headerName: isSmall ? 'Viewr' : 'Viewer',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      maxWidth: 110,
      disableColumnMenu: true,
      valueGetter: (params) => params.row.authorizationProfile.isViewer, // Extract raw value for sorting
      renderCell: (params: GridRenderCellParams) => {
        return params.row.authorizationProfile.isViewer ? (
          <CheckIcon sx={{ fontSize: '18px' }} />
        ) : null;
      },
    },
    {
      field: 'isInstaller',
      headerName: isSmall ? 'Instlr' : 'Installer',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      maxWidth: 110,
      disableColumnMenu: true,
      valueGetter: (params) => params.row.authorizationProfile.isInstaller, // Extract raw value for sorting
      renderCell: (params: GridRenderCellParams) => {
        return params.row.authorizationProfile.isInstaller ? (
          <CheckIcon sx={{ fontSize: '18px' }} />
        ) : null;
      },
    },
    {
      field: 'isManager',
      headerName: isSmall ? 'Mngr' : 'Manager',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      maxWidth: 110,
      disableColumnMenu: true,
      valueGetter: (params) => params.row.authorizationProfile.isManager, // Extract raw value for sorting
      renderCell: (params: GridRenderCellParams) => {
        return params.row.authorizationProfile.isManager ? (
          <CheckIcon sx={{ fontSize: '18px' }} />
        ) : null;
      },
    },
  ];

  const navigate = useNavigate();

  const handleRowClick = (params: GridRowParams) => {
    const { row } = params;
    const uid = row?._id;

    if (uid) {
      navigate(`/users/${uid}/tab/general`);
    } else {
      navigate('/users');
    }
  };

  const rowCount = (data?.authorizedUsers ?? []).length;

  return data ? (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        alignItems: 'stretch',
      }}
    >
      <DataTable
        {...(isSmall ? MobileListDataTableProps : {})}
        getRowId={(user: Partial<User>) => {
          return user._id;
        }}
        sxProps={{
          border: 'none',
          '& .MuiDataGrid-iconButtonContainer': {
            width: isSmall ? '20px !important' : '28px',
          },
          '& .button.MuiButtonBase-root': {
            width: isSmall ? '20px' : undefined,
          },
        }}
        additionalProps={{
          onRowClick: (i: GridRowParams) => handleRowClick(i),
        }}
        hideFooterPagination={rowCount < 51}
        hideFooter={rowCount < 51}
        columns={columns}
        suppressAutoPageSize={true}
        columnVisibilityModel={{
          email: !isSmall,
          isViewer: !isSmall,
          isInstaller: !isSmall,
          isManager: !isSmall,
        }}
        rowHeight={isSmall ? 96 : undefined}
        rows={data.authorizedUsers ?? []}
        initialState={{
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
        }}
      />
    </div>
  ) : null;
}
